import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { LandingLayout } from '@/components/landing/LandingLayout';
import { PainAwareLandingHero } from '@/components/landing/PainAwareLandingHero';
import { PainSolutionBlock } from '@/components/landing/PainSolutionBlock';
import { BeforeAfterScenario } from '@/components/landing/BeforeAfterScenario';
import { ValuePropsSection } from '@/components/landing/ValuePropsSection';
import { BottomCtaBlock } from '@/components/landing/BottomCtaBlock';
import { AiSmartAutomationSection } from '@/components/landing/AiSmartAutomationSection';
import { FaqSection } from '@/components/landing/FaqSection';
import { AiAssistantSummary } from '@/components/landing/AiAssistantSummary';
import { StatsSection } from '@/components/landing/StatsSection';
import { TestimonialCarousel } from '@/components/landing/TestimonialCarousel';
import { StickyFloatingCTA } from '@/components/landing/StickyFloatingCTA';
import { TrustBadges } from '@/components/landing/TrustBadges';
import { ComparisonTable } from '@/components/landing/ComparisonTable';
import { RoiSection } from '@/components/landing/RoiSection';
import { Badge } from '@/components/ui/badge';

const AirbnbCleaningSchedule = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const canonicalUrl = `https://rental.shift-box.com/${currentLang}/airbnb-cleaning-schedule-software`;
  const alternateLanguages = ['en', 'es', 'de', 'fr', 'it'];

  return (
    <>
      <Helmet>
        <title>{t('cleaningSchedule.seo.title')}</title>
        <meta name="description" content={t('cleaningSchedule.seo.description')} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Hreflang tags for multi-language support */}
        {alternateLanguages.map(lang => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={`https://rental.shift-box.com/${lang}/airbnb-cleaning-schedule-software`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href="https://rental.shift-box.com/en/airbnb-cleaning-schedule-software"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                "name": "Rental Manager - Cleaning Schedule Software",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Web"
              },
              {
                "@type": "FAQPage",
                "mainEntity": (t('cleaningSchedule.seo.faq', { returnObjects: true }) as any[]).map((faq: any) => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              }
            ]
          })}
        </script>
      </Helmet>

      <LandingLayout>
        {/* Sticky Floating CTA */}
        <StickyFloatingCTA />

        {/* Hero */}
        <PainAwareLandingHero
          h1={t('cleaningSchedule.hero.h1')}
          subtitle={t('cleaningSchedule.hero.subtitle')}
          ctaText={t('cleaningSchedule.hero.ctaText')}
          ctaAction={() => navigate('/auth')}
          visualContent={
            <div className="relative animate-fade-in">
              <img 
                src="/screenshots/portfolio.webp" 
                alt={t('cleaningSchedule.hero.imageAlt')}
                className="rounded-lg shadow-2xl border border-border"
              />
              <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg animate-scale-in">
                <p className="text-sm font-semibold">{t('cleaningSchedule.hero.badge')}</p>
              </div>
              <Badge className="absolute -top-3 -left-3 animate-pulse">{t('cleaningSchedule.hero.popularBadge')}</Badge>
            </div>
          }
        />

        {/* Trust Badges */}
        <TrustBadges />

        {/* Stats Section */}
        <StatsSection />

        {/* Unique focus paragraph */}
        <section className="py-8 container mx-auto px-4 max-w-3xl">
          <p className="text-lg text-center text-muted-foreground">
            {t('cleaningSchedule.focus')}
          </p>
        </section>

        {/* Pain Section */}
        <section className="py-16 container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">
            {t('cleaningSchedule.pain.title')}
          </h2>
          <PainSolutionBlock
            problemTitle={t('cleaningSchedule.pain.problemTitle')}
            problemBullets={t('cleaningSchedule.pain.problemBullets', { returnObjects: true }) as string[]}
            solutionTitle={t('cleaningSchedule.pain.solutionTitle')}
            solutionBullets={t('cleaningSchedule.pain.solutionBullets', { returnObjects: true }) as string[]}
          />
        </section>

        {/* Before/After Scenario */}
        <section className="py-16 bg-gradient-to-b from-muted/50 to-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">
              {t('cleaningSchedule.beforeAfter.title')}
            </h2>
            <BeforeAfterScenario
              beforeText={t('cleaningSchedule.beforeAfter.before')}
              afterText={t('cleaningSchedule.beforeAfter.after')}
            />
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-4 text-center">
              {t('cleaningSchedule.testimonials.title')}
            </h2>
            <p className="text-center text-muted-foreground mb-12">
              {t('cleaningSchedule.testimonials.subtitle')}
            </p>
            <TestimonialCarousel
              testimonials={t('cleaningSchedule.testimonials.items', { returnObjects: true }) as any[]}
            />
          </div>
        </section>

        {/* AI/Smart Automation with Screenshots */}
        <section className="py-16 bg-gradient-to-r from-primary/5 to-secondary/10">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-8">
              <Badge className="mb-4">{t('cleaningSchedule.automation.badge')}</Badge>
              <h2 className="text-3xl font-bold">{t('cleaningSchedule.automation.title')}</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
              <div>
                <ul className="space-y-4">
                  {(t('cleaningSchedule.automation.features', { returnObjects: true }) as string[]).map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-primary font-bold">{index + 1}</span>
                      </div>
                      <p className="text-sm">{feature}</p>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="relative">
                <img 
                  src="/screenshots/properties.webp" 
                  alt={t('cleaningSchedule.automation.imageAlt')}
                  className="rounded-lg shadow-xl border border-border"
                />
                <div className="absolute -bottom-3 -right-3 bg-primary text-primary-foreground px-3 py-1 rounded-lg shadow-lg text-xs font-semibold">
                  {t('cleaningSchedule.automation.imageBadge')}
                </div>
              </div>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              {t('cleaningSchedule.automation.note')}
            </p>
          </div>
        </section>

        {/* Value Props */}
        <ValuePropsSection />

        {/* ROI Section */}
        <RoiSection />

        {/* Comparison Table */}
        <ComparisonTable
          rows={[
            { feature: t('cleaningSchedule.comparison.features.0'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('cleaningSchedule.comparison.features.1'), manual: false, spreadsheet: true, rentalManager: true },
            { feature: t('cleaningSchedule.comparison.features.2'), manual: false, spreadsheet: true, rentalManager: true },
            { feature: t('cleaningSchedule.comparison.features.3'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('cleaningSchedule.comparison.features.4'), manual: true, spreadsheet: false, rentalManager: true },
            { feature: t('cleaningSchedule.comparison.features.5'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('cleaningSchedule.comparison.features.6'), manual: true, spreadsheet: false, rentalManager: true },
          ]}
        />

        {/* Proof Positioning */}
        <section className="py-16 container mx-auto px-4 text-center">
          <blockquote className="text-xl italic text-muted-foreground max-w-3xl mx-auto border-l-4 border-primary pl-6">
            {t('cleaningSchedule.quote')}
          </blockquote>
        </section>

        {/* FAQ */}
        <FaqSection faqs={t('cleaningSchedule.faq', { returnObjects: true }) as any[]} />

      {/* AI Assistant Summary */}
      <AiAssistantSummary />

      {/* Bottom CTA */}
      <BottomCtaBlock />
      </LandingLayout>
    </>
  );
};

export default AirbnbCleaningSchedule;
